package de.oevbs.kunde.kundenportal2.services.contract

import de.oevbs.businesspartner.bpcrmservice2.common.model.LineOfBusiness
import de.oevbs.businesspartner.bpcrmservice2.contract.model.Contract
import de.oevbs.businesspartner.bpcrmservice2.contract.model.ContractStatus
import de.oevbs.businesspartner.bpcrmservice2.contract.model.RiskAddress
import de.oevbs.businesspartner.bpcrmservice2.contract.model.isNotActive
import de.oevbs.kosmos.ui.icons.LineOfBusinessIcons
import de.oevbs.kosmos.ui.icons.icon
import de.oevbs.kosmos.ui.layouts.card
import dev.fritz2.core.RenderContext
import dev.fritz2.core.Tag
import dev.fritz2.core.joinClasses
import org.w3c.dom.HTMLTableSectionElement

/**
 * Ergänzt ein [Iterable] um ein boolesches Flag, das anzeigt, ob es sich um das letzte Element handelt oder nicht.
 *
 * Die Ausführung ist dabei maximal effizient, so dass eine Laufzeit von O(n) gilt.
 *
 * @return [Pair] des eigentlichen Wertes [T] und einem booleschen Flag, welches `false` ist außer beim letzten Element
 */
fun <T> Iterable<T>.withLastElementFlag(): Iterable<Pair<T, Boolean>> =
    (this + sequenceOf(null)).zipWithNext().map { (value, state) -> value!! to (state == null) }

/**
 * Erzeugt aus einem Double einen € formatierten String
 */
fun Double.toEuro() = this.toString().split(".").let { base ->
    val whole = base[0].reversed().chunked(3).joinToString(".").reversed()
    val decimal = if (base.size == 2) base[1] else "00"
    buildString {
        append("$whole,$decimal")
        if (decimal.length == 1) append("0")
        append(" €")
    }
}

fun Double.toInsuranceSum() = this.toString().split(".").let { base ->
    base[0].reversed().chunked(3).joinToString(".").reversed()
}


fun String?.withBlankDash() = when (this) {
    "" -> "―"
    "0,00 €" -> "―"
    null -> "―"
    else -> this
}

/**
 * gibt Ja/Nein bei true/false zurück
 */
fun Boolean.toJaNein() = if (this) "Ja" else "Nein"

/**
 * Fügt die Felder aus der [RiskAddress] zusammen.
 */
fun RiskAddress.toView(): String = buildString {
    if (streetConcatenated.isNotEmpty()) {
        append(streetConcatenated)
        if (postalCode.isNotEmpty()) {
            append(", ")
        }
    }
    if (postalCode.isNotEmpty()) {
        append("$postalCode ")
    }
    if (city.isNotEmpty()) {
        append(city)
    }
}

/**
 * Tabelle für Vertragsbeteiligte
 *
 * @param entries beliebige Tabellenzeilen
 */
fun RenderContext.detailsPersonRoleTable(entries: Tag<HTMLTableSectionElement>.() -> Unit) {
    table("w-full table-fixed") {
        thead("border-b border-gray-200") {
            tr("text-left") {
                th("p-2") { +"Person" }
                th("p-2") { +"Rollen" }
            }
        }
        tbody {
            entries()
        }
    }
}

/**
 * Tabelleneintrag für die Vertragsbeteiligten
 *
 * @param name
 * @param address
 * @param dateOfBirth
 * @param roles
 */
fun RenderContext.detailsPersonRoleTableEntry(name: String?, address: String?, dateOfBirth: String?, roles: String?) {
    tr {
        td("p-2") {
            b { +name.withBlankDash() }
            br { }
            +address.withBlankDash()
            br { }
            em { +"Geburtsdatum: " }
            +dateOfBirth.withBlankDash()
        }
        td("p-2 align-top") {
            +roles.withBlankDash()
        }
    }
}

/**
 * Vertragstitelzeile
 *
 * @param contractNumber Die Nummer des Vertrags
 * @param lineOfBusiness die Sparte
 * @param status der aktuelle Vertragsstatus
 */
fun RenderContext.contractTitle(
    contractNumber: String,
    lineOfBusiness: LineOfBusiness,
    status: ContractStatus,
) = div(
    joinClasses(
        "flex flex-row gap-2 items-center",
        if (status.isNotActive) "text-gray-500" else "text-primary-700"
    )
) {
    icon("h-14 w-14 md:h-8 md:w-8", icon = LineOfBusinessIcons.valueOf(lineOfBusiness.value))
    h3(if (status.isNotActive) "vui-label-2 md:vui-labelbig-4" else "vui-label-1 md:vui-labelbig-2") {
        if (lineOfBusiness == LineOfBusiness.FDLProdukte)
            +"Ihr Finanzprodukt Nr. $contractNumber"
        else +"Ihre ${lineOfBusiness.title} Nr. $contractNumber"
    }
}

/**
 * Vertragstitelzeile
 *
 * @param contract Der Vertrag
 */
fun RenderContext.contractTitle(contract: Contract) =
    contractTitle(contract.contractNumber, contract.lineOfBusiness, contract.status)

/**
 * Detail Sektion
 *
 * @param title der Titel einer Section
 * @param details der Inhalte einer Section
 */
fun RenderContext.detailsSection(
    title: String? = null,
    details: RenderContext.() -> Unit
) {
    title?.let { h4("vui-label-3 mt-3") { +it } }
    card {
        details()
    }
}

/**
 * Detail Sektion
 *
 * @param title der Titel einer Section
 * @param details der Inhalte einer Section
 */
fun RenderContext.detailsSectionBlank(
    title: String,
    details: RenderContext.() -> Unit
) {
    h4("vui-label-3") { +title }
    details()
}
